import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoadingState, LoadingEvent } from '../app/quoteAssist-V2/models/component-events.model';

/**
 * Loading Service for managing application-wide loading states
 * 
 * This service provides a centralized way to manage loading states across the application.
 * It supports different types of loading indicators:
 * - 'full-page': Full page overlay with spinner (like V1)
 * - 'component': Component-level loading
 * - 'button': Button-level loading with spinner
 * 
 * Usage Examples:
 * 
 * 1. Basic loading:
 *    this.loadingService.showLoading('Processing...');
 *    // ... do work ...
 *    this.loadingService.hideLoading();
 * 
 * 2. Action-specific loading:
 *    this.loadingService.showActionLoading('quote submission', 'Submitting your quote...');
 *    // ... do work ...
 *    this.loadingService.hideLoading();
 * 
 * 3. Wrapper for async operations:
 *    this.loadingService.withLoading(
 *      this.apiService.saveData(),
 *      'Saving data...',
 *      'full-page'
 *    ).subscribe(result => {
 *      // Handle result
 *    });
 * 
 * 4. Component-level loading:
 *    this.loadingService.showLoading('Loading component...', 'component');
 * 
 * 5. Button loading:
 *    this.loadingService.showLoading('Submitting...', 'button');
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly _loadingState$ = new BehaviorSubject<LoadingState>({
    isLoading: false,
    loadingMessage: '',
    loadingType: 'full-page'
  });

  readonly loadingState$ = this._loadingState$.asObservable();

  /**
   * Show loading with custom message and type
   * @param message - Optional loading message to display
   * @param type - Type of loading indicator ('full-page', 'component', 'button')
   */
  showLoading(message?: string, type: 'full-page' | 'component' | 'button' = 'full-page'): void {
    this._loadingState$.next({
      isLoading: true,
      loadingMessage: message || 'Loading...',
      loadingType: type
    });
  }

  /**
   * Hide loading
   */
  hideLoading(): void {
    this._loadingState$.next({
      isLoading: false,
      loadingMessage: '',
      loadingType: 'full-page'
    });
  }

  /**
   * Show loading for a specific action
   * @param action - The action being performed (e.g., 'quote submission', 'data save')
   * @param message - Optional custom message
   * @param type - Type of loading indicator
   */
  showActionLoading(action: string, message?: string, type: 'full-page' | 'component' | 'button' = 'full-page'): void {
    this.showLoading(message || `Processing ${action}...`, type);
  }

  /**
   * Get current loading state
   * @returns Current LoadingState object
   */
  getCurrentLoadingState(): LoadingState {
    return this._loadingState$.value;
  }

  /**
   * Check if currently loading
   * @returns boolean indicating if loading is active
   */
  isCurrentlyLoading(): boolean {
    return this._loadingState$.value.isLoading;
  }

  /**
   * Create a loading wrapper for async operations
   * Automatically shows loading at start and hides on completion/error
   * 
   * @param operation - The Observable operation to wrap
   * @param message - Loading message to display
   * @param type - Type of loading indicator
   * @returns Observable that automatically manages loading state
   */
  withLoading<T>(
    operation: Observable<T>,
    message?: string,
    type: 'full-page' | 'component' | 'button' = 'full-page'
  ): Observable<T> {
    this.showLoading(message, type);
    
    return new Observable(observer => {
      operation.subscribe({
        next: (value) => {
          this.hideLoading();
          observer.next(value);
        },
        error: (error) => {
          this.hideLoading();
          observer.error(error);
        },
        complete: () => {
          this.hideLoading();
          observer.complete();
        }
      });
    });
  }
}